import { useState, useMemo } from 'react';
import { useExecutiveQuery, QueryRequest } from '@/hooks/use-executive-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, Search, ArrowUpDown, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useExportData } from '@/hooks/use-executive-query';
import { toast } from 'sonner';

export interface ExecutiveDataGridProps {
  defaultLimit?: number;
}

export function ExecutiveDataGrid({ defaultLimit = 50 }: ExecutiveDataGridProps) {
  const [queryParams, setQueryParams] = useState<QueryRequest>({
    select: ['*'],
    limit: defaultLimit,
    offset: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('transaction_time');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, isError } = useExecutiveQuery(queryParams);
  const exportData = useExportData();

  // Update query params when filters change
  const handleSearch = () => {
    setQueryParams((prev) => ({
      ...prev,
      filter: searchTerm
        ? {
            $or: [
              { transaction_id: { $contains: searchTerm } },
              { order_id: { $contains: searchTerm } },
            ],
          }
        : undefined,
      offset: 0, // Reset to first page
    }));
  };

  const handleSort = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    setQueryParams((prev) => ({
      ...prev,
      sortBy: [{ field, direction: newDirection }],
    }));
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    setQueryParams((prev) => ({
      ...prev,
      offset: direction === 'next'
        ? (prev.offset || 0) + (prev.limit || defaultLimit)
        : Math.max(0, (prev.offset || 0) - (prev.limit || defaultLimit)),
    }));
  };

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      await exportData.mutateAsync({ format, timeRange: '30d' });
      toast.success(`Data exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  // Extract table data
  const tableData = useMemo(() => {
    if (!data?.data) return { rows: [], columns: [] };

    const rows = Array.isArray(data.data) ? data.data : data.data.data || [];
    const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

    return { rows, columns };
  }, [data]);

  const currentPage = Math.floor((queryParams.offset || 0) / (queryParams.limit || defaultLimit)) + 1;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-4">
        <div className="flex-1 flex items-center gap-2">
          <Input
            placeholder="Search by transaction ID or order ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="max-w-md"
          />
          <Button onClick={handleSearch} size="sm">
            <Search className="h-4 w-4 mr-2" />
            Search
          </Button>
        </div>

        {/* Data tier badge */}
        {data?.tier && (
          <Badge variant={data.tier === 'hot' ? 'default' : data.tier === 'warm' ? 'secondary' : 'outline'}>
            {data.tier.toUpperCase()} Tier
          </Badge>
        )}

        {/* Export buttons */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
            disabled={exportData.isPending}
          >
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('json')}
            disabled={exportData.isPending}
          >
            <Download className="h-4 w-4 mr-2" />
            JSON
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {tableData.columns.map((col) => (
                  <TableHead key={col} className="bg-gray-50">
                    <button
                      onClick={() => handleSort(col)}
                      className="flex items-center gap-1 hover:text-gray-900"
                    >
                      {col.replace(/_/g, ' ').toUpperCase()}
                      <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={tableData.columns.length} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={tableData.columns.length} className="text-center py-8 text-red-600">
                    Failed to load data
                  </TableCell>
                </TableRow>
              ) : tableData.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableData.columns.length} className="text-center py-8 text-gray-500">
                    No data available
                  </TableCell>
                </TableRow>
              ) : (
                tableData.rows.map((row: any, idx: number) => (
                  <TableRow key={idx}>
                    {tableData.columns.map((col) => (
                      <TableCell key={col} className="font-mono text-xs">
                        {formatCellValue(row[col])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Page {currentPage} • Showing {tableData.rows.length} rows
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange('prev')}
            disabled={!queryParams.offset || queryParams.offset === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange('next')}
            disabled={tableData.rows.length < (queryParams.limit || defaultLimit)}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Query analysis */}
      {data?.analysis && (
        <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
          <span className="font-medium">Query Analysis:</span> {data.analysis.level} complexity
          ({(data.analysis.confidence * 100).toFixed(0)}% confidence)
          {data.analysis.reasons.length > 0 && ` • ${data.analysis.reasons.join(', ')}`}
        </div>
      )}
    </div>
  );
}

// Helper: Format cell values
function formatCellValue(value: any): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'number') return new Intl.NumberFormat('id-ID').format(value);

  // Format dates
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString('id-ID');
    }
  }

  return String(value);
}
